import type { FilterGroup, FilterRange } from "@/composites/product-listing";
import { CATEGORY_PATH_SEPARATOR } from "@/domain/product-listing/category-path";
import { LISTING_TOGGLE_VALUES } from "@/domain/product-listing/listing-facets";

import type { ListingFilterDefinition } from "./product-listing-content";

export type FacetCounts = Record<string, Record<string, number>>;
export type FacetStats = Record<string, { min: number; max: number }>;

export interface ListingFacts {
	total: number;
	counts: FacetCounts;
	stats: FacetStats;
}

export interface FacetSelection {
	lists: Record<string, readonly string[]>;
	toggles: Record<string, boolean>;
	ranges: Record<string, FilterRange>;
}

export const NO_SELECTION: FacetSelection = {
	lists: {},
	toggles: {},
	ranges: {}
};

export interface RangeFormat {
	locale: string;
	formatOptions: Intl.NumberFormatOptions;
}

const DEFAULT_MAX_SHOWN = 5;

function leafLabel(value: string): string {
	return value.split(CATEGORY_PATH_SEPARATOR).at(-1) ?? value;
}

// Unauthored options come from the results, most products first. The order
// follows the unfiltered listing, so options don't move while the shopper filters.
function listValues(
	filter: ListingFilterDefinition,
	countByValue: Record<string, number>,
	baseline: Record<string, number>
): Array<{ value: string; label: string }> {
	if (filter.values.length > 0) return filter.values;

	const order = (value: string) =>
		baseline[value] ?? countByValue[value] ?? 0;
	return [
		...new Set([...Object.keys(baseline), ...Object.keys(countByValue)])
	]
		.map((value) => ({ value, label: leafLabel(value) }))
		.sort(
			(a, b) =>
				order(b.value) - order(a.value) ||
				a.label.localeCompare(b.label)
		);
}

function toGroup(
	filter: ListingFilterDefinition,
	facts: ListingFacts,
	selection: FacetSelection,
	rangeFormat: RangeFormat,
	baseline: FacetCounts
): FilterGroup[] {
	const countByValue = facts.counts[filter.attribute] ?? {};

	switch (filter.kind) {
		case "list": {
			const selected = selection.lists[filter.attribute] ?? [];
			const values = listValues(
				filter,
				countByValue,
				baseline[filter.attribute] ?? {}
			).map((option) => ({
				...option,
				count: countByValue[option.value] ?? 0,
				selected: selected.includes(option.value)
			}));
			return [
				{
					kind: "list",
					id: filter.id,
					label: filter.label,
					maxShown: filter.maxShown ?? DEFAULT_MAX_SHOWN,
					values
				}
			];
		}
		case "toggle": {
			const isOn = selection.toggles[filter.attribute] === true;
			const count = (
				LISTING_TOGGLE_VALUES[filter.attribute] ?? []
			).reduce((total, value) => total + (countByValue[value] ?? 0), 0);
			return [
				{
					kind: "toggle",
					id: filter.id,
					label: filter.label,
					value: {
						value: filter.attribute,
						label: filter.values[0]?.label ?? filter.label ?? "",
						count,
						selected: isOn
					}
				}
			];
		}
		case "range": {
			const bounds = facts.stats[filter.attribute];
			if (!bounds) return [];
			return [
				{
					kind: "range",
					id: filter.id,
					label: filter.label,
					bounds: {
						min: Math.floor(bounds.min),
						max: Math.ceil(bounds.max)
					},
					selected: selection.ranges[filter.attribute] ?? {},
					...rangeFormat
				}
			];
		}
	}
}

export function toFilterGroups(
	filters: ListingFilterDefinition[],
	facts: ListingFacts,
	selection: FacetSelection,
	rangeFormat: RangeFormat,
	baseline: FacetCounts = facts.counts
): FilterGroup[] {
	return filters.flatMap((filter) =>
		toGroup(filter, facts, selection, rangeFormat, baseline)
	);
}
